import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";

export const T_STATUS = { abierto: ["pendiente", "Abierto"], en_atencion: ["info", "En atención"], resuelto: ["activo", "Resuelto"], cerrado: ["inactivo", "Cerrado"], reabierto: ["bloqueado", "Reabierto"] };
export const CATS = [{ id: "sistema", name: "Sistema" }, { id: "equipos", name: "Equipos" }, { id: "accesos", name: "Accesos" }, { id: "otro", name: "Otro" }];
export const PRIOS = [{ id: "baja", name: "Baja" }, { id: "media", name: "Media" }, { id: "alta", name: "Alta" }, { id: "critica", name: "Crítica" }];
const FILTERS = [{ id: "activos", name: "Activos" }, { id: "all", name: "Todos" }, ...Object.entries(T_STATUS).map(([k, v]) => ({ id: k, name: v[1] }))];
const PRIO_CLS = { baja: "text-[#5b6656]", media: "text-[#1d4f91]", alta: "text-[#8a5a00]", critica: "text-[#b42318] font-bold" };

const NewTicket = ({ open, onClose, onCreated }) => {
  const [f, setF] = useState({ title: "", category: "sistema", priority: "media", description: "" });
  useEffect(() => { if (open) setF({ title: "", category: "sistema", priority: "media", description: "" }); }, [open]);
  const save = async () => { try { const { data } = await api.post("/support/tickets", f); toast.success(`${data.code} creado`); onCreated(data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>Nuevo ticket de soporte</DialogTitle></DialogHeader>
        <Field label="Título *"><Input data-testid="ticket-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoría"><Pick testid="ticket-category-select" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATS} /></Field>
          <Field label="Prioridad"><Pick testid="ticket-priority-select" value={f.priority} onChange={(v) => setF({ ...f, priority: v })} options={PRIOS} /></Field>
        </div>
        <Field label="Descripción *" hint="Puede adjuntar archivos o capturas en el ticket una vez creado."><Textarea data-testid="ticket-description-input" rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <DialogFooter><Button data-testid="ticket-create-btn" className="rounded-full" disabled={f.title.trim().length < 4 || f.description.trim().length < 5} onClick={save}>Crear ticket</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Tickets() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const staff = can("admin.tickets.atender");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("activos");
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/support/tickets", { params: { status: status === "all" ? "" : status } }).then((r) => setItems(r.data)); }, [status]);
  return (
    <div data-testid="tickets-page">
      <PageHeader eyebrow="Comunicación" title="Soporte" subtitle={staff ? "Todos los tickets de soporte. Usted atiende como Administrador del Sistema." : "Sus tickets de soporte al Administrador del Sistema."}
        actions={<Button data-testid="new-ticket-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={15} className="mr-1" />Nuevo ticket</Button>} />
      <div className="mb-4 w-48"><Pick testid="ticket-filter-select" value={status} onChange={setStatus} options={FILTERS} /></div>
      <div className="syc-card overflow-hidden" data-testid="tickets-list">
        {items.length === 0 ? <EmptyState title="Sin tickets" /> : items.map((t) => (
          <button key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => navigate(`/soporte/${t.id}`)} className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 border-b border-[#f0f2ee] last:border-0 hover:bg-[#f6fbf2] text-sm text-left">
            <span className="font-mono text-xs w-28">{t.code}</span>
            <span className="flex-1 min-w-[180px] font-semibold">{t.title}</span>
            <span className={`text-xs capitalize ${PRIO_CLS[t.priority]}`}>{PRIOS.find((p) => p.id === t.priority)?.name}</span>
            {staff && <span className="text-xs text-[#5b6656]">{t.created_by_name}</span>}
            <span className="text-xs text-[#8a9584]">{fmtDateTime(t.updated_at)}</span>
            <StatusBadge status={T_STATUS[t.status][0]} label={T_STATUS[t.status][1]} />
          </button>))}
      </div>
      <NewTicket open={open} onClose={() => setOpen(false)} onCreated={(t) => navigate(`/soporte/${t.id}`)} />
    </div>
  );
}
